import Image from 'next/image'

// صورة القطعة، وبديلها المرسوم حين لا توجد صورة.
//
// البديل يُرسم في الواجهة ولا يُكتب في قاعدة البيانات أبداً:
// `product.thumbnail IS NULL` هو ما نعرف به أيّ القطع ينقصها صورة حقيقية
// (١٩٥٦ اليوم)، وهو شرط مشترك `auto-thumbnail` حين تصل صور المورّد.
// فلو كُتب البديل في القاعدة خسرنا العدّاد وتوقّف ضبط الصور تلقائياً.
//
// والبديل مرسوم لا مصوَّر عمداً: مسطّح بلون مجموعته وإطار متقطّع،
// فلا يظنّه الزبون صورة القطعة نفسها.
//
// (١٤ أكتوبر) الأيقونة تُختار من اسم القطعة أولاً — «كوشنتي» رمان بلي لا نابض —
// ثم من المجموعة إن لم يُعرف الاسم. واللون يبقى لون المجموعة دائماً.

type Props = {
  src?: string | null
  alt?: string
  /** `group_name` كما في `catalog_part_type` — يحدّد اللون، والأيقونة إن لم يُعرف الاسم */
  group?: string | null
  className?: string
  sizes?: string
}

type Look = { label: string; ink: string; icon: JSX.Element; art: string }

const S = {
  fill: 'none' as const,
  strokeWidth: 0.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

// ───────── أيقونات المجموعات ─────────

// كتلة محرك — عامّة للمجموعة، لا تشبه قطعة بعينها
const engineBlock = (
  <g>
    <path d="M5 9h2.5V7h3.5V5.5h4V7h2.5l2 2H21v6h-1.5l-2 2.5H8L6.5 15H5z" />
    <path d="M5 11H2.5v2H5M21 11h1.5" />
    <path d="M11 7h4" />
  </g>
)

// بستم — رأسه بحلقاته، ومحور، وذراع إلى رأس الكرنك
const piston = (
  <g>
    <rect x="6.5" y="2.5" width="11" height="9" rx="1" />
    <path d="M6.5 5h11M6.5 7h11" />
    <circle cx="12" cy="9.3" r="1" />
    <path d="M11.3 10.2L10 17.2M12.7 10.2L14 17.2" />
    <circle cx="12" cy="19" r="2.5" />
    <circle cx="12" cy="19" r=".8" />
  </g>
)

// نابض — لفّات حلزونية بين صحنين
const spring = (
  <g>
    <path d="M6.5 3.5h11M6.5 20.5h11" />
    {[6.6, 10, 13.4, 16.8].map((y) => (
      <ellipse key={y} cx="12" cy={y} rx="5" ry="1.5" transform={`rotate(-8 12 ${y})`} />
    ))}
  </g>
)

// برق
const bolt = <path d="M13.5 2.5L6 13h5l-1 8.5L18 10h-5.5z" />

// رادياتير — خزّانان وزعانف بينهما وغطاء
const radiator = (
  <g>
    <rect x="4" y="4" width="16" height="2.5" rx=".8" />
    <rect x="4" y="17.5" width="16" height="2.5" rx=".8" />
    <path d="M5 6.5v11M19 6.5v11" />
    <path d="M5 9.5 7 8.4 9 9.5 11 8.4 13 9.5 15 8.4 17 9.5 19 8.4" />
    <path d="M5 12.6 7 11.5 9 12.6 11 11.5 13 12.6 15 11.5 17 12.6 19 11.5" />
    <path d="M5 15.7 7 14.6 9 15.7 11 14.6 13 15.7 15 14.6 17 15.7 19 14.6" />
    <path d="M15.5 4V2.5h2V4M20 18.75h2" />
  </g>
)

// عتلة التعشيق
const shifter = (
  <g>
    <path d="M6.5 5v8M17.5 5v8M6.5 9h11" />
    <path d="M12 9v9.5" />
    <circle cx="12" cy="20" r="1.8" />
  </g>
)

// ديسكو وكاليبر
const brake = (
  <g>
    <circle cx="12" cy="12" r="7.5" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M18.8 8.3h2.4a1 1 0 011 1v5.4a1 1 0 01-1 1h-2.4z" />
  </g>
)

// مضخة بنزين في محطة الوقود
const drop = (
  <g>
    <rect x="4" y="3" width="10" height="18" rx="1.2" />
    <rect x="6" y="5.5" width="6" height="4" rx=".5" />
    <path d="M2.5 21h13" />
    <path d="M14 8h1.8a1.5 1.5 0 011.5 1.5v7a1.35 1.35 0 002.7 0V8.6L17.6 6.2" />
  </g>
)

// دريكسيون
const wheel = (
  <g>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M12 3.5v5.9M4.7 16.2l5.1-2.9M19.3 16.2l-5.1-2.9" />
  </g>
)

// هيكل سيارة
const body = (
  <g>
    <path d="M4.6 16.5H3a.5.5 0 01-.5-.5v-2.6a1.5 1.5 0 011.1-1.45L7 11l2.6-3.1a2 2 0 011.5-.7h4.4a2 2 0 011.5.7L19.6 11l1.5.4a1.5 1.5 0 011.1 1.45V16a.5.5 0 01-.5.5h-2" />
    <path d="M8.8 16.5h6.8M7.2 11h12.2M13.2 7.3V11" />
    <circle cx="6.7" cy="16.5" r="2.1" />
    <circle cx="17.7" cy="16.5" r="2.1" />
  </g>
)

// بلّورة ثلج
const snow = (
  <g>
    <path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9" />
    <path d="M9.6 5.4L12 3l2.4 2.4M9.6 18.6L12 21l2.4-2.4" />
  </g>
)

// صامولة — الافتراضي لأي مجموعة لم تُعرَّف
const nut = (
  <g>
    <path d="M12 3l7.8 4.5v9L12 21l-7.8-4.5v-9z" />
    <circle cx="12" cy="12" r="3.2" />
  </g>
)

// ───────── أيقونات أنواع القطع ─────────

// كوبيركو بنتارية — غطاء رأس المحرك: غطاء مقبّب بمسامير وفتحة زيت
const valveCover = (
  <g>
    <path d="M3 16h18l-1.6-6.2A1.5 1.5 0 0017.9 8.6H6.1a1.5 1.5 0 00-1.5 1.2z" />
    <path d="M2 18.5h20" />
    <path d="M10.5 8.6V6.5h3v2.1" />
    <circle cx="6.8" cy="13.2" r=".7" />
    <circle cx="17.2" cy="13.2" r=".7" />
  </g>
)

// حساس — فيشة، وصامولة سداسية، وجسم مسنّن، ورأس قياس
const sensor = (
  <g>
    <rect x="9" y="2.5" width="6" height="4" rx=".8" />
    <path d="M7.5 6.5h9l1 3-1 3h-9l-1-3z" />
    <path d="M9.5 12.5h5v5h-5z" />
    <path d="M9.5 14h5M9.5 15.7h5" />
    <path d="M11 17.5v3.5h2v-3.5" />
  </g>
)

// كوشنتي — رمان بلي: حلقتان وكرات بينهما
const bearing = (
  <g>
    <circle cx="12" cy="12" r="8.8" />
    <circle cx="12" cy="12" r="3.4" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
      <circle
        key={a}
        cx={12 + 6.1 * Math.cos((a * Math.PI) / 180)}
        cy={12 + 6.1 * Math.sin((a * Math.PI) / 180)}
        r="1.25"
      />
    ))}
  </g>
)

// برنزيني — نصف محمل سبائكي
const shell = (
  <g>
    <path d="M3.5 14a8.5 8.5 0 0117 0" />
    <path d="M7 14a5 5 0 0110 0" />
    <path d="M3.5 14H7M17 14h3.5" />
    <path d="M12 9v-1" />
  </g>
)

// قرسيوني — وجه سلندر بفتحاته
const gasket = (
  <g>
    <rect x="2" y="7" width="20" height="10" rx="2" />
    <circle cx="6" cy="12" r="1.7" />
    <circle cx="10.7" cy="12" r="1.7" />
    <circle cx="15.3" cy="12" r="1.7" />
    <path d="M19.2 9.6v.01M19.2 14.4v.01" />
  </g>
)

// برولي — صوفة / مانع تسرّب
const seal = (
  <g>
    <circle cx="12" cy="12" r="8.8" />
    <circle cx="12" cy="12" r="4.4" />
    <circle cx="12" cy="12" r="6.6" strokeDasharray="1.6 1.8" />
  </g>
)

// بوجي / شمعة تسخين
const plug = (
  <g>
    <path d="M10.6 2.5h2.8v2.6h-2.8z" />
    <path d="M9.6 5.1h4.8l-.6 5.9h-3.6z" />
    <path d="M8.2 11h7.6v3.2H8.2z" />
    <path d="M10.4 14.2h3.2v4h-3.2z" />
    <path d="M12 18.2v2.6h1.8" />
  </g>
)

// باطني — قماش فرامل على صفيحته
const pad = (
  <g>
    <path d="M3 9.2c4-2.8 14-2.8 18 0l-1.3 6.6c-4.5-2.1-10.9-2.1-15.4 0z" />
    <path d="M6.2 10.6c3.7-1.5 7.9-1.5 11.6 0l-.7 3.4c-3.3-1.1-6.9-1.1-10.2 0z" />
    <path d="M12 10.1v3.2" />
    <circle cx="4.9" cy="9.9" r=".6" />
    <circle cx="19.1" cy="9.9" r=".6" />
  </g>
)

// فلتر — علبة فلتر زيت بقاعدتها وحزّات المسك
const filter = (
  <g>
    <rect x="5.5" y="5" width="13" height="2.5" rx=".8" />
    <path d="M10 5V3h4v2" />
    <path d="M6.5 7.5h11v11a2 2 0 01-2 2h-7a2 2 0 01-2-2z" />
    <path d="M6.5 11h11M6.5 15h11" />
    <path d="M8.5 18v2.3M11 18v2.5M13.5 18v2.5M16 18v2.3" />
  </g>
)

// منكوطي — خرطوم بمشبكين
const hose = (
  <g>
    <path d="M3 6h5a5 5 0 015 5v1.5a3.5 3.5 0 003.5 3.5H21" />
    <path d="M3 10h5a1 1 0 011 1v1.5a7.5 7.5 0 007.5 7.5H21" />
    <path d="M5.5 5v6M18.5 15v6" />
  </g>
)

// مضخة — مروحة داخلية ومخرج
const pump = (
  <g>
    <circle cx="11" cy="13" r="7" />
    <circle cx="11" cy="13" r="2" />
    <path d="M11 6v5M18 13h-5M11 20v-5M4 13h5" />
    <path d="M15.5 7.6V4h5v4" />
  </g>
)

// مروحة — ثلاث ريش حول محور
const fanBlade = <path d="M12 9.8C10.6 6.4 11.4 3.3 13.8 3.6c2.3.3 1.9 3.6-1.8 6.2z" />
const fan = (
  <g>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="1.9" />
    {fanBlade}
    <g transform="rotate(120 12 12)">{fanBlade}</g>
    <g transform="rotate(240 12 12)">{fanBlade}</g>
  </g>
)

// صبرطو — قطعة واحدة: مطاط ملتحم بين كتيفة علوية وقاعدة بمسامير
const mount = (
  <g>
    <rect x="3" y="18" width="18" height="2.5" rx=".7" />
    <circle cx="5" cy="19.25" r=".5" />
    <circle cx="19" cy="19.25" r=".5" />
    <path d="M6 18c.5-4.2 2.6-7.5 6-7.5s5.5 3.3 6 7.5" />
    <rect x="8.5" y="7.5" width="7" height="3" rx=".6" />
    <path d="M12 7.5V4M9.5 15c1.6-.8 3.4-.8 5 0" />
  </g>
)

// شنقة — سير على بكرتين
const belt = (
  <g>
    <circle cx="7" cy="14.5" r="4.2" />
    <circle cx="17.5" cy="7.5" r="3" />
    <path d="M5.2 10.7l11-6.1M10.9 16.2l9.2-6.9" />
    <circle cx="7" cy="14.5" r="1" />
    <circle cx="17.5" cy="7.5" r=".8" />
  </g>
)

// كاتينة — جنزير مغلق بيضاوي على ترسين
const chain = (
  <g>
    <rect x="2.5" y="6.5" width="19" height="11" rx="5.5" />
    <rect x="4.5" y="8.5" width="15" height="7" rx="3.5" />
    <path d="M8 6.5v2M10.7 6.5v2M13.3 6.5v2M16 6.5v2M8 15.5v2M10.7 15.5v2M13.3 15.5v2M16 15.5v2" />
    <circle cx="8" cy="12" r="1.8" />
    <circle cx="16" cy="12" r="1.8" />
  </g>
)

// ترمستاتة — مقياس حرارة
const thermo = (
  <g>
    <path d="M10 14.2V5a2 2 0 014 0v9.2a4 4 0 11-4 0z" />
    <path d="M12 9v8" />
  </g>
)

// لمبة
const bulb = (
  <g>
    <path d="M8.5 13.6a5 5 0 117 0c-.6.6-1 1.3-1 2.1v.3h-5v-.3c0-.8-.4-1.5-1-2.1z" />
    <path d="M10.2 10.6l.9-1.4.9 1.4.9-1.4.9 1.4" />
    <path d="M9.5 18h5M10 20.3h4" />
  </g>
)

const LOOKS: Record<string, Look> = {
  engine: { label: 'المحرك', ink: '#2F5597', icon: engineBlock, art: 'engine_block' },
  // التعليق كمجموعة: رسمة المنظومة كاملة؛ والمساعد وحده لما اسمه «مزطوري/مساعد»
  suspension: { label: 'التعليق', ink: '#9E5718', icon: spring, art: 'suspension_assembly' },
  electrical: { label: 'الكهرباء', ink: '#846310', icon: bolt, art: 'battery' },
  cooling: { label: 'التبريد', ink: '#1C6B79', icon: radiator, art: 'radiator' },
  transmission: { label: 'ناقل الحركة', ink: '#573A92', icon: shifter, art: 'clutch_kit' },
  brakes: { label: 'الفرامل', ink: '#96292B', icon: brake, art: 'brake_disc' },
  fuel: { label: 'الوقود', ink: '#2C6633', icon: drop, art: 'fuel_pump' },
  steering: { label: 'التوجيه', ink: '#1C6755', icon: wheel, art: 'steering_rack' },
  bodywork: { label: 'الهيكل', ink: '#475569', icon: body, art: 'body_panel' },
  hvac: { label: 'التكييف', ink: '#265D8C', icon: snow, art: 'ac_compressor' },
  other: { label: 'قطعة', ink: '#5B5B55', icon: nut, art: 'nut_bolt' },
}

// المطابقة بالاسم الحرفي أولاً، ثم بكلمة دالّة — فمجموعة جديدة
// أو معادة الصياغة تجد لونها بلا تعديل هذا الملف.
const EXACT: Record<string, string> = {
  'المحرك': 'engine',
  'نظام التعليق': 'suspension',
  'الكهرباء والإلكترونيات': 'electrical',
  'نظام التبريد': 'cooling',
  'ناقل الحركة والقابض': 'transmission',
  'نظام الفرامل': 'brakes',
  'نظام الوقود': 'fuel',
  'نظام التوجيه': 'steering',
  'الهيكل الخارجي': 'bodywork',
  'نظام التكييف والتدفئة': 'hvac',
}

const KEYWORDS: [string, string][] = [
  ['تعليق', 'suspension'],
  ['كهرب', 'electrical'],
  ['إلكترون', 'electrical'],
  ['تبريد', 'cooling'],
  ['ناقل', 'transmission'],
  ['قابض', 'transmission'],
  ['فرامل', 'brakes'],
  ['فرملة', 'brakes'],
  ['وقود', 'fuel'],
  ['توجيه', 'steering'],
  ['هيكل', 'bodywork'],
  ['تكييف', 'hvac'],
  ['تدفئة', 'hvac'],
  ['محرك', 'engine'],
]

// أيقونة من اسم القطعة بالدارجة. الترتيب مقصود: الأدقّ أولاً،
// فـ«برولي كوشنتي» صوفة لا رمان بلي، و«قرسيوني مضخة» جوان لا مضخة،
// و«ديسكو باطني» قرص لا قماش.
// ولا تُضاف هنا كلمة قصيرة تظهر داخل كلمات أخرى («سير» تطابق «سيراتو»).
const PART_ICONS: [string[], JSX.Element, string][] = [
  [['ديسكو'], brake, 'brake_disc'],
  [['برولي', 'باروليو', 'صوفة'], seal, 'seal'],
  [['قرسيوني', 'قرسيون', 'جوان'], gasket, 'gasket'],
  [['كوبيركو', 'كوبركو'], valveCover, 'valve_cover'],
  [['حساس', 'حسّاس', 'حسَّاس'], sensor, 'sensor'],
  [['بسطوني', 'بستم', 'بستون'], piston, 'piston'],
  [['شماعي', 'شمعة', 'بوجي', 'بواجي'], plug, 'spark_plug'],
  [['كوشنتي', 'كوشنت'], bearing, 'bearing'],
  [['مزطوري', 'مساعد'], spring, 'coil_spring'],
  [['برنزيني', 'برنزين'], shell, 'bearing_shell'],
  [['باطني'], pad, 'brake_pad'],
  // «فلتر» وحدها خمسة أنواع، فالنوع من الكلمة التي بعدها.
  [['فلتر مكيف', 'فلتر مكيّف', 'فلتر تكييف'], filter, 'cabin_filter'],
  [['فلتر هواء'], filter, 'air_filter'],
  [['فلتر بنزين', 'فلتر نافته', 'فلتر ديزل', 'فلتر سولار', 'فلتر وقود'], filter, 'fuel_filter'],
  [['فلتر كمبيو', 'فلتر قير'], filter, 'transmission_filter'],
  [['فلتر'], filter, 'oil_filter'],
  [['منكوطي'], hose, 'hose'],
  [['ترمستاتة', 'ترمستات', 'ترموستات'], thermo, 'thermostat'],
  // «مضخة» وحدها اسم لستّ قطع مختلفة، فالنوع من الكلمة التي بعدها.
  // و«مضخة» بلا وصف تأخذ رسمة مجموعتها (في نظام الوقود: مضخة البنزين).
  [['مضخة بنزين', 'طرمبة بنزين', 'مضخة وقود', 'قلب مضخة'], pump, 'fuel_pump'],
  [['مضخة زيت', 'طرمبة زيت'], pump, 'oil_pump'],
  [['دراوليك'], pump, 'power_steering_pump'],
  [['فرسيوني'], pump, 'clutch_master'],
  [['مضخة فرامل', 'طرمبة فرامل'], pump, 'brake_master'],
  [['مضخة ماء', 'مضخة مياه', 'طرمبة ماء', 'طرمبة مياه'], pump, 'water_pump'],
  [['مروحة'], fan, 'fan'],
  [['صبرطو'], mount, 'engine_mount'],
  [['كاتينة', 'كتينة'], chain, 'timing_chain'],
  [['شنقة'], belt, 'belt'],
  [['لمبة', 'لمبات'], bulb, 'bulb'],
  [['مبرد مكيف', 'مبرد مكيّف', 'مكثف', 'كوندنسر'], radiator, 'condenser'],
  [['غطاء مبرد', 'غطاء رادياتير'], radiator, 'radiator_cap'],
  [['مبرد', 'رادياتير'], radiator, 'radiator'],
]

const partOfName = (name?: string | null): { icon: JSX.Element; art: string } | null => {
  const n = (name ?? '').trim()
  if (!n) return null
  for (const [words, icon, art] of PART_ICONS) if (words.some((w) => n.includes(w))) return { icon, art }
  return null
}

// الرسوم شبه الواقعية في `public/parts-art/<art>.svg`، بالأسلوب نفسه لكل الأنواع.
// نوع بلا رسمة هنا يبقى بأيقونته المسطّحة.
const ART = new Set<string>([
  'suspension_assembly',
  'air_filter',
  'cabin_filter',
  'condenser',
  'fuel_filter',
  'radiator_cap',
  'transmission_filter',
  'brake_master',
  'clutch_master',
  'oil_pump',
  'power_steering_pump',
  'ac_compressor',
  'battery',
  'bearing',
  'bearing_shell',
  'belt',
  'body_panel',
  'brake_disc',
  'brake_pad',
  'bulb',
  'clutch_kit',
  'coil_spring',
  'engine_block',
  'engine_mount',
  'fan',
  'fuel_pump',
  'gasket',
  'hose',
  'nut_bolt',
  'oil_filter',
  'piston',
  'radiator',
  'seal',
  'sensor',
  'spark_plug',
  'steering_rack',
  'thermostat',
  'timing_chain',
  'valve_cover',
  'water_pump',
])

const lookOf = (group?: string | null): Look => {
  const g = (group ?? '').trim()
  if (!g) return LOOKS.other
  const exact = EXACT[g]
  if (exact) return LOOKS[exact]
  for (const [word, key] of KEYWORDS) if (g.includes(word)) return LOOKS[key]
  return LOOKS.other
}

export function PartPlaceholder({
  group,
  name,
}: {
  group?: string | null
  /** اسم القطعة — إن عُرف نوعها أخذت أيقونته بدل أيقونة المجموعة */
  name?: string | null
}) {
  const look = lookOf(group)
  const part = partOfName(name)
  const art = part?.art ?? look.art
  const icon = part?.icon ?? look.icon

  // رسمة شبه واقعية إن وُجدت للنوع، وعليها «رسمة توضيحية» كي لا يظنّها
  // الزبون صورة القطعة نفسها. تختفي الكلمة في الإطار الصغير (globals.css).
  if (ART.has(art)) {
    return (
      <div
        role="img"
        aria-label={`رسمة توضيحية — ${look.label}`}
        className="part-art relative flex h-full w-full items-center justify-center"
        style={{ backgroundColor: 'rgb(var(--brand-25))' }}
      >
        <img
          src={`/parts-art/${art}.svg`}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-[82%] max-h-[26rem] w-auto max-w-[82%] object-contain"
        />
        <span className="part-art-tag absolute bottom-1 left-1.5 text-[10px] text-gray-400">رسمة توضيحية</span>
      </div>
    )
  }

  // خلفية واحدة هادئة لكل المجموعات، ولون المجموعة في الخطّ فقط.
  // ومبنيّ بـHTML لا بمربّع SVG: يبقى الرسم كاملاً في وسط أيّ إطار،
  // عريضاً كان (الكتالوج) أو مربّعاً (البطاقات).
  // ولا يُكتب اسم المجموعة داخل الصورة: البطاقة تكتبه تحت اسم القطعة.
  return (
    <div
      role="img"
      aria-label={`لا توجد صورة لهذه القطعة — ${look.label}`}
      className="flex h-full w-full items-center justify-center"
      style={{ backgroundColor: 'rgb(var(--brand-25))', color: look.ink }}
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="h-[62%] max-h-24 min-h-6 w-auto"
        stroke="currentColor"
        {...S}
      >
        {icon}
      </svg>
    </div>
  )
}

export default function PartImage({ src, alt, group, className, sizes }: Props) {
  return (
    <div
      className={
        className ?? 'relative aspect-square w-full overflow-hidden rounded-lg bg-ui-bg-subtle'
      }
    >
      {src ? (
        <Image
          src={src}
          alt={alt ?? ''}
          fill
          className="object-contain"
          sizes={sizes ?? '(max-width: 768px) 50vw, 25vw'}
        />
      ) : (
        <PartPlaceholder group={group} name={alt} />
      )}
    </div>
  )
}
